// Bulk Import Page
import { useState, useEffect, useCallback } from 'react';
import portalApi from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { Upload, Loader2, FileSpreadsheet, Download, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

export function BulkImportPage() {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [onDuplicate, setOnDuplicate] = useState('skip');
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const y = await portalApi.getCoverageYears();
      const accessible = y.filter(yr => yr.accessible !== false && !yr.is_archived);
      setYears(accessible);
      const def = accessible.find(yr => yr.is_default) || accessible[0];
      if (def) setSelectedYear(def.id);
    } catch (err) {
      toast.error(err.message);
    }
  }, []);

  useEffect(() => {
    document.title = 'Bulk Import · Latinos Salud';
    load();
  }, [load]);

  const handleFileSelect = async (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    setResult(null);
    setLoading(true);
    try {
      const data = await portalApi.previewImport(selectedFile);
      setPreview(data);
    } catch (err) {
      toast.error(err.message);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    if (!selectedYear) { toast.error('Select a coverage year'); return; }
    if (!preview?.rows?.length) { toast.error('No rows to import'); return; }
    setLoading(true);
    try {
      const res = await portalApi.commitImport(selectedYear, onDuplicate, preview.rows);
      setResult(res);
      toast.success(`Imported: ${res.created} created, ${res.updated} updated, ${res.skipped} skipped`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="bulk-import-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Bulk Import</h1>
          <p className="text-slate-500 mt-1">Import cases from a CSV file</p>
        </div>
        <Button variant="outline" asChild data-testid="download-template-btn">
          <a href={portalApi.getImportTemplateUrl()} download><Download className="h-4 w-4 mr-2" />Template</a>
        </Button>
      </div>

      <div className="bg-white rounded-xl p-6 border border-slate-200 space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Target Coverage Year *</Label>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger data-testid="import-year-select"><SelectValue placeholder="Select year" /></SelectTrigger>
              <SelectContent>{years.map(y => <SelectItem key={y.id} value={y.id}>{y.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>On Duplicate Athena ID</Label>
            <Select value={onDuplicate} onValueChange={setOnDuplicate}>
              <SelectTrigger data-testid="on-duplicate-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="skip">Skip existing</SelectItem>
                <SelectItem value="update">Update existing</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <label className="block border-2 border-dashed border-slate-200 rounded-xl p-10 text-center cursor-pointer hover:border-sky-300 transition-colors">
          <input type="file" accept=".csv" className="hidden" onChange={(e) => handleFileSelect(e.target.files[0])} data-testid="import-file-input" />
          <FileSpreadsheet className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">{file ? file.name : 'Click to select a CSV file'}</p>
          <p className="text-xs text-slate-400 mt-1">Only .csv files are supported</p>
        </label>
      </div>

      {loading && <div className="flex items-center justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-sky-500" /></div>}

      {preview && !loading && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <p className="font-medium text-slate-900">Preview · {preview.row_count} rows</p>
            <Button onClick={handleCommit} disabled={loading} data-testid="commit-import-btn"><Upload className="h-4 w-4 mr-2" />Import {preview.row_count} rows</Button>
          </div>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>{preview.headers?.map(h => <th key={h} className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-2 whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {preview.rows.slice(0, 50).map((row, i) => (
                  <tr key={`row-${row.athena_id || ''}-${i}`}>
                    {preview.headers?.map(h => <td key={h} className="px-4 py-2 text-slate-600 whitespace-nowrap">{row[h]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="import-result">
          <div className="flex items-center gap-2 mb-4"><CheckCircle2 className="h-5 w-5 text-green-600" /><h3 className="font-semibold text-slate-900">Import Complete</h3></div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-4 bg-green-50 rounded-lg"><p className="text-2xl font-bold text-green-600">{result.created}</p><p className="text-sm text-slate-500">Created</p></div>
            <div className="text-center p-4 bg-sky-50 rounded-lg"><p className="text-2xl font-bold text-sky-600">{result.updated}</p><p className="text-sm text-slate-500">Updated</p></div>
            <div className="text-center p-4 bg-slate-50 rounded-lg"><p className="text-2xl font-bold text-slate-600">{result.skipped}</p><p className="text-sm text-slate-500">Skipped</p></div>
          </div>
          {result.errors?.length > 0 && (
            <div className="space-y-1">
              <p className="flex items-center gap-2 text-sm font-medium text-red-600"><AlertCircle className="h-4 w-4" />{result.errors.length} errors</p>
              {result.errors.slice(0, 10).map((e) => <p key={`err-${e.row}`} className="text-xs text-slate-500">Row {e.row}: {e.error}</p>)}
            </div>
          )}
        </div>
      )}

      <DemographicsUpdater />
      <RepairImportedCases />
    </div>
  );
}

function RepairImportedCases() {
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [done, setDone] = useState(null);

  if (user?.role !== 'admin') return null;

  const runPreview = async () => {
    setLoading(true); setDone(null);
    try { setPreview(await portalApi.backfillLsCases(true)); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  const apply = async () => {
    setApplying(true);
    try {
      const r = await portalApi.backfillLsCases(false);
      setDone(r); setPreview(null);
      toast.success(`Repaired ${r.workspace_fixed} case(s); linked/created ${r.clients_linked} client(s)`);
    } catch (e) { toast.error(e.message); } finally { setApplying(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-amber-200 p-6 mt-8" data-testid="repair-imported-cases">
      <div className="flex items-center gap-2 mb-1">
        <AlertCircle className="h-5 w-5 text-amber-500" />
        <h2 className="text-lg font-semibold text-slate-900">Repair Imported Cases (Latinos Salud)</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Admin maintenance: finds Latinos Salud cases that were imported without a workspace assignment or
        client link (so they don't appear in the Cases list and can't be enriched), assigns the Latinos Salud
        workspace and creates/links a client by Athena ID. Non-destructive and idempotent.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={runPreview} disabled={loading} data-testid="repair-preview-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Check affected cases
        </Button>
        {preview && preview.orphan_cases > 0 && (
          <Button onClick={apply} disabled={applying} data-testid="repair-apply-btn">
            {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Repair {preview.orphan_cases} case{preview.orphan_cases === 1 ? '' : 's'}
          </Button>
        )}
      </div>
      {preview && (
        <p className="mt-3 text-sm text-slate-600" data-testid="repair-preview-result">
          Found <strong>{preview.orphan_cases}</strong> case(s) to repair — {preview.workspace_fixed} need a workspace, {preview.clients_linked} need a client.
          {preview.orphan_cases === 0 ? ' Nothing to repair.' : ''}
        </p>
      )}
      {done && (
        <p className="mt-3 text-sm text-green-600 flex items-center gap-2" data-testid="repair-done">
          <CheckCircle2 className="h-4 w-4" /> Repaired {done.workspace_fixed} case(s); {done.clients_linked} client(s) linked/created.
        </p>
      )}
    </div>
  );
}

function DemographicsUpdater() {
  const [csvText, setCsvText] = useState('');
  const [filename, setFilename] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const reset = () => { setCsvText(''); setFilename(''); setSummary(null); };

  const onFile = async (f, inputEl) => {
    if (!f) return;
    const text = await f.text();
    setCsvText(text); setFilename(f.name); setSummary(null);
    if (inputEl) inputEl.value = '';
    setLoading(true);
    try { setSummary(await portalApi.bulkClientDemographics(text, true, f.name)); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const r = await portalApi.bulkClientDemographics(csvText, false, filename);
      const total = (r.clients_to_update || 0) + (r.cases_to_update || 0) + (r.policies_to_update || 0);
      toast.success(`Applied: ${r.clients_to_update} client, ${r.cases_to_update} case, ${r.policies_to_update} policy updates`);
      setSummary(r);
      if (total === 0) toast.info('No changes were needed.');
    } catch (e) { toast.error(e.message); } finally { setApplying(false); }
  };

  const changedRows = (summary?.rows || []).filter(
    (r) => r.status === 'Matched' && (r.has_change || (r.warnings && r.warnings.length))
  );
  const notMatched = (summary?.rows || []).filter((r) => r.status === 'Not Matched');
  const errorRows = (summary?.rows || []).filter((r) => r.status === 'Error');
  const canApply = summary && !summary.applied &&
    ((summary.clients_to_update || 0) + (summary.cases_to_update || 0) + (summary.policies_to_update || 0)) > 0;

  const DiffGroup = ({ title, items }) => (
    items && items.length ? (
      <div className="mt-2">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</p>
        <div className="mt-1 space-y-1">
          {items.map((c, i) => (
            <div key={`${title}-${c.field}-${i}`} className="text-sm flex flex-wrap items-center gap-2">
              <span className="text-slate-500 min-w-[130px]">{c.label}:</span>
              <span className="text-slate-400 line-through">{c.old || '—'}</span>
              <span className="text-slate-400">→</span>
              <span className="font-medium text-slate-900">{c.new}</span>
              {c.note ? <span className="text-xs text-amber-600">{c.note}</span> : null}
            </div>
          ))}
        </div>
      </div>
    ) : null
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mt-8" data-testid="demographics-updater">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-sky-500" />
          <h2 className="text-lg font-semibold text-slate-900">Client / Case / Policy Data Enrichment (Latinos Salud)</h2>
        </div>
        <Button variant="outline" size="sm" asChild data-testid="download-enrich-template-btn">
          <a href={portalApi.getEnrichTemplateUrl()} download><Download className="h-4 w-4 mr-2" />Download Template</a>
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Bulk-enrich existing Latinos Salud records matched by <strong>Athena ID</strong>. Updates Client profile,
        Case and Policy fields. <strong>Non-destructive</strong>: blank cells never erase existing data. Never creates
        clients or cases; policies are only updated (never created). A field-by-field preview is shown before you confirm.
      </p>
      <label className="inline-flex items-center gap-2 cursor-pointer bg-slate-100 hover:bg-slate-200 px-4 py-2 rounded-lg text-sm font-medium">
        <Upload className="h-4 w-4" />
        Choose CSV
        <input type="file" accept=".csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0], e.target)} data-testid="demo-csv-input" />
      </label>
      {filename && <span className="ml-3 text-sm text-slate-500">{filename}</span>}

      {loading && <div className="flex items-center gap-2 text-slate-500 mt-4"><Loader2 className="h-4 w-4 animate-spin" /> Analyzing…</div>}

      {summary && (
        <div className="mt-5" data-testid="demo-summary">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {[['Total', summary.total_rows, 'text-slate-700'], ['Matched', summary.matched, 'text-green-600'],
              ['Not Matched', summary.not_matched, 'text-amber-600'], ['Errors', summary.errors, 'text-red-600'],
              ['Clients', summary.clients_to_update, 'text-sky-600'], ['Cases', summary.cases_to_update, 'text-sky-600'],
              ['Policies', summary.policies_to_update, 'text-sky-600'], ['Review', summary.review, 'text-amber-600']].map(([l, v, c]) => (
              <div key={l} className="border border-slate-200 rounded-lg p-3 text-center">
                <p className={`text-2xl font-bold ${c}`}>{v ?? 0}</p><p className="text-xs text-slate-400">{l}</p>
              </div>
            ))}
          </div>

          {summary.applied && (
            <p className="mt-4 text-sm text-green-600 flex items-center gap-2" data-testid="demo-applied">
              <CheckCircle2 className="h-4 w-4" /> Updates applied.
            </p>
          )}

          {!summary.applied && changedRows.length > 0 && (
            <div className="mt-5 space-y-3" data-testid="demo-diff-list">
              <p className="text-sm font-medium text-slate-700">Proposed changes ({changedRows.length} record{changedRows.length === 1 ? '' : 's'}):</p>
              <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                {changedRows.slice(0, 100).map((r, idx) => (
                  <div key={`${r.client_id || r.athena_id}-${idx}`} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-semibold text-slate-900">{r.calyvra_client_id || 'Client'}</span>
                      <span className="text-slate-400">· Athena {r.athena_id}</span>
                    </div>
                    <DiffGroup title="Client Profile" items={r.changes?.client} />
                    <DiffGroup title="Case" items={r.changes?.case} />
                    <DiffGroup title="Policy" items={r.changes?.policy} />
                    {r.warnings?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {r.warnings.map((w, i) => (
                          <span key={i} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />{w}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {notMatched.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-amber-700 mb-1">Not matched ({notMatched.length}) — no action taken:</p>
              <div className="flex flex-wrap gap-1">
                {notMatched.slice(0, 40).map((r, i) => (
                  <span key={i} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded">{r.athena_id}</span>
                ))}
              </div>
            </div>
          )}

          {errorRows.length > 0 && (
            <p className="mt-3 text-sm text-red-600 flex items-center gap-2"><AlertCircle className="h-4 w-4" />{errorRows.length} row(s) with errors (missing Athena ID).</p>
          )}

          {!summary.applied && (
            <div className="mt-5 flex items-center gap-3">
              {canApply && (
                <Button onClick={apply} disabled={applying} data-testid="demo-apply-btn">
                  {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Confirm & Apply Updates
                </Button>
              )}
              <Button variant="ghost" onClick={reset} disabled={applying} data-testid="demo-cancel-btn">Cancel</Button>
              {!canApply && <span className="text-sm text-slate-400">No changes to apply.</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BulkImportPage;
